import React from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/joy';
import {
    VisibilityFilledRounded,
    EditFilledRounded,
    VisibilityOffFilledRounded,
} from '@mui-symbols-material/w300';
import GridViewAsset from '../../Svg/GridViewAsset';
import { GridAsset, Maybe } from '../../../codegen/graphql';
import { useMutation } from '@apollo/client';
import { UpdateGridItem } from '../../../graphql/mutations/updateGridItem';
import EditGridItemModal from '../EditGridItemModal';
import AssetNameplate from '../../AssetNameplate';
import { Tooltip } from '@mui/material';
import { IOT_STATUS_COLOR_MAP } from '../../../config/iotConnectionStatusConfig';

interface Props {
    gridItemId?: string;
    title?: string | null;
    gridAssets: Maybe<GridAsset>[];
    isEditMode: boolean;
    editModalOpen: boolean;
    onEditOpen: () => void;
    onEditClose: () => void;
    isVisible: boolean;
}

const GridBox = ({
                     gridItemId,
                     title,
                     gridAssets,
                     isEditMode,
                     editModalOpen,
                     onEditOpen,
                     onEditClose,
                     isVisible,
                 }: Props) => {
    const theme = useTheme();
    const [updateGridItemMutation] = useMutation(UpdateGridItem);

    const handleChangeVisibility = () => {
        updateGridItemMutation({
            variables: {
                id: gridItemId,
                input: { visible: !isVisible },
            },
            optimisticResponse: {
                updateGridItem: {
                    __typename: 'GridItem',
                    id: gridItemId,
                    visible: !isVisible,
                },
            },
            refetchQueries: ['GridDetail'],
        });
    };

    const sortedAssets = [...gridAssets]
        .filter((a): a is GridAsset => a != null)
        .sort((a, b) => (a.sortId ?? 0) - (b.sortId ?? 0));

    return (
        <Box
            border={isEditMode ? '1px solid #ccc' : '1px solid transparent'}
            borderRadius="3px"
            bgcolor={isEditMode ? '#E5E5E5' : 'transparent'}
            height="100%"
            display="flex"
            flexDirection="column"
            p="8px"
            sx={{ overflow: 'hidden' }}
        >
            {/* Title Row */}
            <Box
                display="flex"
                alignItems="center"
                borderBottom={`2px solid ${theme.palette.primary[500]}`}
                pb={0.5}
                mb={1}
            >
                <Typography
                    level="titleMedium"
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {title}
                </Typography>

                <Box display="flex" gap={0.5} ml={1} flexShrink={0} sx={{ visibility: isEditMode ? 'visible' : 'hidden' }}>
                    <IconButton
                        className="no-drag"
                        size="sm"
                        variant="soft"
                        color="primary"
                        onClick={handleChangeVisibility}
                    >
                        {isVisible ? <VisibilityFilledRounded /> : <VisibilityOffFilledRounded />}
                    </IconButton>

                    <IconButton
                        className="no-drag"
                        size="sm"
                        variant="soft"
                        color="primary"
                        disabled={gridAssets?.length === 0}
                        onClick={onEditOpen}
                    >
                        <EditFilledRounded />
                    </IconButton>
                </Box>
            </Box>

            {/* Asset-Container */}
            <Box
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="nowrap"
                sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
            >
                {sortedAssets.map((gridAsset, index) => {
                    if (gridAsset?.visible || isEditMode) {
                        const assetLocationId = gridAsset?.installedBaseNode?.assetLocation?.id;
                        const status =
                            gridAsset?.installedBaseNode?.assetLocation?.iotConnection?.connectionStatus ||
                            'OFF';

                        return (
                            <Tooltip
                                key={gridAsset?.id || index}
                                title={
                                    assetLocationId ? (
                                        <Box sx={{ p: 1, width: 287 }}>
                                            <AssetNameplate
                                                assetLocationId={assetLocationId}
                                                hideImageActions
                                            />
                                        </Box>
                                    ) : (
                                        ''
                                    )
                                }
                                arrow={false}
                                placement="right"
                                enterDelay={200}
                                leaveDelay={200}
                                disableInteractive={false}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: 'background.paper',
                                            color: 'text.primary',
                                            boxShadow: 3,
                                            borderRadius: 1,
                                            p: 0,
                                        },
                                    },
                                }}
                            >
                                <Box sx={{ display: 'inline-flex', px: 0.5, py: 0.5 }}>
                                    <GridViewAsset
                                        width={28}
                                        fill={isEditMode ? IOT_STATUS_COLOR_MAP['OFF'] : IOT_STATUS_COLOR_MAP[status]}
                                        style={{ cursor: assetLocationId ? 'pointer' : 'default' }}
                                    />
                                </Box>
                            </Tooltip>
                        );
                    }
                    return null;
                })}
            </Box>

            {editModalOpen && (
                <EditGridItemModal
                    gridAssets={gridAssets}
                    title={title}
                    open={editModalOpen}
                    onClose={onEditClose}
                />
            )}
        </Box>
    );
};

export default GridBox;
